import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, Button, Box, TextField
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useState, useContext } from 'react';
import { DateRangeContext } from '../../../Context.js';
import axios from 'axios';
import { get_Call_Count } from '../../../constants.js';

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const CallSummaryTable = ({ summaryData }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { filterType, start, end } = useContext(DateRangeContext);

  // 🔥 Transform API response to component format
  const transformApiData = (apiResponse) => {
    if (!apiResponse.success || !apiResponse.data || !apiResponse.data.monthlyData) {
      return [];
    }

    const { monthlyData } = apiResponse.data;
    
    // Create transformed data structure
    const transformedData = [
      {
        status: "Intrested (Hot+Warm)",
        date: dayjs().format('YYYY-MM-DD'), // Use current date
        months: {},
        total: 0
      },
      {
        status: "Not Instrested (Cold/Lost)", 
        date: dayjs().format('YYYY-MM-DD'),
        months: {},
        total: 0
      },
      {
        status: "No Call Established(Pending)",
        date: dayjs().format('YYYY-MM-DD'),
        months: {},
        total: 0
      }
    ];

    // Initialize months with 0 values
    months.forEach(month => {
      transformedData[0].months[month.toLowerCase()] = 0; // Hot/Warm
      transformedData[1].months[month.toLowerCase()] = 0; // Cold/Lost
      transformedData[2].months[month.toLowerCase()] = 0; // Pending
    });

    // Fill data from API response
    monthlyData.forEach(monthData => {
      const monthKey = monthData.month.toLowerCase();
      
      if (transformedData[0].months.hasOwnProperty(monthKey)) {
        // Hot/Warm data
        transformedData[0].months[monthKey] = monthData.hotWarm || 0;
        transformedData[0].total += monthData.hotWarm || 0;
        
        // Cold/Lost data
        transformedData[1].months[monthKey] = monthData.coldLost || 0;
        transformedData[1].total += monthData.coldLost || 0;
        
        // Pending data
        transformedData[2].months[monthKey] = monthData.pending || 0;
        transformedData[2].total += monthData.pending || 0;
      }
    });

    return transformedData;
  };

  // 🔥 Updated API function with backend filter integration
  const fetchDataFromAPI = async (currentFilterType = null, customStart = null, customEnd = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use current filter type or fall back to context filter type
      const activeFilterType = currentFilterType || filterType;
      const activeStart = customStart || start;
      const activeEnd = customEnd || end;

      // 🔥 Build query parameters based on filter type
      const params = {};
      
      switch (activeFilterType) {
        case 'today':
          params.filterType = 'today';
          break;
        case 'yesterday':
          params.filterType = 'yesterday';
          break;
        case 'last7':
          params.filterType = 'last7';
          break;
        case 'month':
          params.filterType = 'month';
          break;
        case 'custom':
          if (activeStart && activeEnd) {
            params.filterType = 'custom';
            params.customStartDate = dayjs(activeStart).format('YYYY-MM-DD');
            params.customEndDate = dayjs(activeEnd).format('YYYY-MM-DD');
          } else {
            // Fallback to no filter if custom dates are missing
            params.filterType = 'none';
          }
          break;
        case 'none':
        default:
          params.filterType = 'none';
          break;
      }

      console.log('Fetching data with params:', params);

      // 🔥 API call with filter parameters
      const response = await axios.get(`${get_Call_Count}`, {
        params: params,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('API Response:', response.data);

      // Transform and set data
      const transformed = transformApiData(response.data);
      setData(transformed);
      setFilteredData(transformed);
      
    } catch (error) {
      console.error('Error fetching data from API:', error);
      
      // Enhanced error handling
      if (error.response) {
        setError(`Server Error: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        setError('Network Error: Cannot reach server. Make sure backend is running.');
      } else {
        setError(`Error: ${error.message}`);
      }
      
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔥 Updated useEffect to handle both props and API data with backend filtering
  useEffect(() => {
    if (Array.isArray(summaryData) && summaryData.length > 0) {
      // Use props data if provided
      setData(summaryData);
      setFilteredData(summaryData);
    } else {
      // Fetch from API with current filter settings
      fetchDataFromAPI();
    }
  }, [summaryData]);

  // 🔥 Updated filter effect - now triggers API call instead of client-side filtering
  useEffect(() => {
    // Only fetch from API if we don't have props data
    if (!summaryData || summaryData.length === 0) {
      fetchDataFromAPI(filterType, start, end);
    } else {
      // If we have props data, apply client-side filtering (legacy behavior)
      applyClientSideFilter();
    }
  }, [filterType, start, end]);

  // 🔥 Client-side filtering (used only when props data is provided)
  const applyClientSideFilter = () => {
    if (!data || data.length === 0) return;
    
    const today = dayjs();
    let out = data;

    if (filterType === 'today') out = data.filter(d => dayjs(d.date).isSame(today, 'day'));
    if (filterType === 'yesterday') out = data.filter(d => dayjs(d.date).isSame(today.subtract(1, 'day'), 'day'));
    if (filterType === 'last7') out = data.filter(d => dayjs(d.date).isBetween(today.subtract(6, 'day'), today, null, '[]'));
    if (filterType === 'month') out = data.filter(d => dayjs(d.date).isSame(today, 'month'));
    if (filterType === 'custom') out = data.filter(d => dayjs(d.date).isBetween(dayjs(start), dayjs(end), null, '[]'));
    if (filterType === 'none') out = data;

    setFilteredData(out);
  };

  // 🔥 Updated applyFilter function - now calls backend
  const applyFilter = (newFilterType) => {
    fetchDataFromAPI(newFilterType);
  };

  // 🔥 Updated custom range function - now calls backend
  const applyCustomRange = () => {
    if (customRange.start && customRange.end) {
      fetchDataFromAPI('custom', customRange.start, customRange.end);
    }
  };

  const grandTotals = filteredData.reduce((acc, row) => {
    months.forEach(month => {
      const key = month.toLowerCase();
      acc[key] = (acc[key] || 0) + (row.months?.[key] ?? 0);
    });
    acc.total = (acc.total || 0) + (row.total ?? 0);
    return acc;
  }, {});

  // Enhanced Excel download function with full table data
  const downloadExcel = () => {
    // Create comprehensive header with metadata
    const timestamp = dayjs().format('YYYY-MM-DD HH:mm:ss');
    const filterInfo = getFilterDescription();
    
    // Main table headers
    const header = ['Call Category', ...months, 'Grand Total'];
    
    // Prepare data rows with filtered data
    const dataRows = filteredData.map(row => {
      const rowData = [row.status];
      months.forEach(month => {
        rowData.push(row.months?.[month.toLowerCase()] ?? 0);
      });
      rowData.push(row.total ?? 0);
      return rowData;
    });

    // Add grand total row
    const grandTotalRow = ['Grand Total'];
    months.forEach(month => {
      grandTotalRow.push(grandTotals[month.toLowerCase()] ?? 0);
    });
    grandTotalRow.push(grandTotals.total ?? 0);

    // Combine all rows with metadata
    const allRows = [
      ['Call Summary Dashboard - Monthly Call Counts by Category'],
      [`Generated on: ${timestamp}`],
      [`Filter Applied: ${filterInfo}`],
      [`Total Categories: ${filteredData.length}`],
      [], // Empty row for spacing
      header,
      ...dataRows,
      [], // Empty row before grand total
      grandTotalRow,
      [], // Empty row for spacing
    ];

    // Generate enhanced CSV content
    let csvContent = 'data:text/csv;charset=utf-8,\uFEFF'; // UTF-8 BOM for Excel compatibility
    csvContent += allRows.map(row => 
      Array.isArray(row) 
        ? row.map(cell => `"${cell}"`).join(',') 
        : `"${row}"`
    ).join('\n');

    // Create and trigger download
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `call_summary_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to describe current filter
  const getFilterDescription = () => {
    if (filterType === 'today') return 'Today';
    if (filterType === 'yesterday') return 'Yesterday';
    if (filterType === 'last7') return 'Last 7 Days';
    if (filterType === 'month') return 'Current Month';
    if (filterType === 'custom') return `Custom Range: ${start} to ${end}`;
    return 'All Data (No Filter)';
  };

  // 🔥 Updated refresh function
  const refreshData = () => {
    fetchDataFromAPI(filterType, start, end);
  };

  return (
    <>
      {/* Simple Summary Table */}
      <TableContainer
        component={Paper}
        sx={{
          mt: 3,
          boxShadow: 2
        }}
      >
        {/* Header with Title on Left and Download Button on Right */}
        <Box
          sx={{
            backgroundColor: '#097969',
            color: 'white',
            py: 2,
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              textAlign: 'left'
            }}
          >
             Call Summary Dashboard - Monthly Call Counts by Category
            {loading && ' (Loading...)'}
            {error && ' (Error loading data)'}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* 🔥 Refresh Button */}
            <Button
              variant="outlined"
              onClick={refreshData}
              disabled={loading}
              sx={{
                color: 'white',
                borderColor: 'white',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white'
                }
              }}
            >
              🔄 Refresh
            </Button>
            
            <Button
              variant="contained"
              color="success"
              onClick={downloadExcel}
              disabled={filteredData.length === 0 || loading}
              sx={{
                backgroundColor: '#195f35ff',
                fontWeight: 'bold',
                px: 3,
                py: 1,
                '&:hover': {
                  backgroundColor: '#45a049',
                  transform: 'translateY(-1px)'
                },
                '&:disabled': {
                  backgroundColor: '#cccccc',
                  color: '#666'
                },
                transition: 'all 0.2s ease'
              }}
            >
              📥 Export 
            </Button>
          </Box>
        </Box>

        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold', borderBottom: '2px solid #ddd' }}>
                Call Category
              </TableCell>
              {months.map((month) => (
                <TableCell
                  key={month}
                  align="center"
                  sx={{ fontWeight: 'bold', borderBottom: '2px solid #ddd' }}
                >
                  {month.slice(0, 3)}
                </TableCell>
              ))}
              <TableCell
                align="center"
                sx={{ fontWeight: 'bold', borderBottom: '2px solid #ddd' }}
              >
                Grand Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={14}
                  align="center"
                  sx={{
                    py: 3,
                    color: '#666'
                  }}
                >
                  Loading call data...
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={14}
                  align="center"
                  sx={{
                    py: 3,
                    color: '#666'
                  }}
                >
                  {error ? `Error: ${error}` : 'No call data found for the selected filters'}
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filteredData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: '#fafafa'
                      },
                      '&:hover': {
                        backgroundColor: '#e3f2fd'
                      }
                    }}
                  >
                    <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                      {row.status === '(Hot+Warm)' && 'Intrested'}
                      {row.status === 'Pending' && '⏳'}
                      {row.status === 'Cold/Lost' && '❄️'} {row.status}
                    </TableCell>
                    {months.map((month) => (
                      <TableCell
                        key={month}
                        align="center"
                        sx={{ borderBottom: '1px solid #eee' }}
                      >
                        {row.months?.[month.toLowerCase()] ?? 0}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{
                        borderBottom: '1px solid #eee',
                        fontWeight: 'bold'
                      }}
                    >
                      {row.total ?? 0}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Grand Total Row */}
                <TableRow
                  sx={{
                    backgroundColor: '#e3f2fd',
                    fontWeight: 'bold'
                  }}
                >
                  <TableCell sx={{ borderTop: '2px solid #ddd' }}>
                    Grand Total
                  </TableCell>
                  {months.map((month) => (
                    <TableCell
                      key={month}
                      align="center"
                      sx={{ borderTop: '2px solid #ddd' }}
                    >
                      {grandTotals[month.toLowerCase()] ?? 0}
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{ borderTop: '2px solid #ddd' }}
                  >
                    {grandTotals.total ?? 0}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CallSummaryTable;
